import { useCallback, useEffect, useMemo, useState } from 'react';
import { documentService } from '@services/document.service';
import { formatDocumentTitle } from '@utils/documentUtils';
import { DASHBOARD } from '@constants/dashboard';
import type { DocumentSummary } from '../types/dashboard';

interface DashboardStats {
  totalDocuments: number;
  recentUploads: number;
  lastUpdated: string | null;
}

interface DashboardData {
  stats: DashboardStats;
  recentDocuments: DocumentSummary[];
  loading: boolean;
  error: boolean;
}

const uploadDateOf = (doc: any): string => doc.uploadedAt || doc.createdAt;

const toSummary = (doc: any): DocumentSummary => ({
  id: doc.id,
  title: formatDocumentTitle(doc.title, doc.fileName),
  category: doc.category || 'General',
  categoryName: doc.categoryName,
  uploadedBy: doc.createdByName || doc.createdBy || doc.uploadedBy || 'Unknown',
  uploadedAt: uploadDateOf(doc),
  fileType: doc.mimeType?.split('/').pop() || 'other',
  size: doc.fileSize || 0,
});

export function useDashboardData(): DashboardData {
  const [documents, setDocuments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await documentService.getDocuments({
        page: 1,
        pageSize: DASHBOARD.FETCH_PAGE_SIZE,
        sortBy: 'uploadedAt',
        sortOrder: 'desc',
      });
      const results = response.results ?? [];
      setDocuments(results);
      setTotal(response.total ?? results.length);
      setError(false);
    } catch {
      setDocuments([]);
      setTotal(0);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => {
    const now = new Date();
    const dates = documents.map(uploadDateOf);
    const since = now.getTime() - DASHBOARD.RECENT_UPLOAD_WINDOW_DAYS * 86_400_000;

    return {
      stats: {
        totalDocuments: total,
        recentUploads: dates.filter((value) => new Date(value).getTime() >= since).length,
        lastUpdated: documents.length > 0 ? uploadDateOf(documents[0]) : null,
      },
      recentDocuments: documents.slice(0, DASHBOARD.RECENT_DOCUMENT_COUNT).map(toSummary),
      loading,
      error,
    };
  }, [documents, total, loading, error]);
}
